import { cornerRadius } from "@bug-on/m3-tokens";
import type { Transition } from "motion/react";
import { FAST_EFFECTS_SPRING } from "../../shared/constants";
import type {
	SplitButtonSize,
	SplitButtonSizeTokens,
} from "./split-button-types";

// Standard (non-expressive) spring for chevron rotation per M3 spec
export const SPLIT_BUTTON_CHEVRON_SPRING: Transition = {
	type: "spring",
	stiffness: 200,
	damping: 20,
	mass: 1,
};

// Use fast effects spring (critically damped, no bounce) for border radius morphing
export const SPLIT_BUTTON_RADIUS_SPRING: Transition = FAST_EFFECTS_SPRING;

/**
 * Split Button Tokens (Expressive)
 * Source: SplitButtonXSmallTokens.kt, SplitButtonSmallTokens.kt,
 *         SplitButtonMediumTokens.kt, SplitButtonLargeTokens.kt,
 *         SplitButtonXLargeTokens.kt (Android/Compose reference)
 */
export const SPLIT_BUTTON_TOKENS = {
	xs: {
		containerHeight: 32, // XSmallTokens: ContainerHeight = 32.0.dp
		betweenSpace: 2, // XSmallTokens: BetweenSpace = 2.0.dp
		innerCorner: {
			default: cornerRadius.extraSmall, // 4dp
			hovered: cornerRadius.small, // 8dp
			pressed: cornerRadius.small, // 8dp
		},
		outerCornerPercent: 50, // XSmallTokens: OuterCornerCornerSizePercent = 50.0f → full pill
		leadingPadding: {
			start: 12, // XSmallTokens: LeadingButtonLeadingSpace = 12.0.dp
			end: 10, // XSmallTokens: LeadingButtonTrailingSpace = 10.0.dp
		},
		trailingPadding: {
			start: 13, // XSmallTokens: TrailingButtonLeadingSpace = 13.0.dp
			end: 13, // XSmallTokens: TrailingButtonTrailingSpace = 13.0.dp
		},
		trailingIconSize: 22, // XSmallTokens: TrailingIconSize = 22.0.dp
		opticalCenterOffset: -1, // Spec: XS = -1dp from center
	},
	sm: {
		containerHeight: 40, // SmallTokens: ContainerHeight = 40.0.dp
		betweenSpace: 2, // SmallTokens: BetweenSpace = 2.0.dp
		innerCorner: {
			default: cornerRadius.extraSmall, // 4dp
			hovered: cornerRadius.medium, // 12dp
			pressed: cornerRadius.medium, // 12dp
		},
		outerCornerPercent: 50,
		leadingPadding: {
			start: 16, // SmallTokens: LeadingButtonLeadingSpace = 16.0.dp
			end: 12, // SmallTokens: LeadingButtonTrailingSpace = 12.0.dp
		},
		trailingPadding: {
			start: 13, // SmallTokens: TrailingButtonLeadingSpace = 13.0.dp
			end: 13, // SmallTokens: TrailingButtonTrailingSpace = 13.0.dp
		},
		trailingIconSize: 22, // SmallTokens: TrailingIconSize = 22.0.dp
		opticalCenterOffset: -1, // Spec: S = -1dp from center
	},
	md: {
		containerHeight: 56, // MediumTokens: ContainerHeight = 56.0.dp
		betweenSpace: 2, // MediumTokens: BetweenSpace = 2.0.dp
		innerCorner: {
			default: cornerRadius.extraSmall, // 4dp
			hovered: cornerRadius.medium, // 12dp
			pressed: cornerRadius.medium, // 12dp
		},
		outerCornerPercent: 50,
		leadingPadding: {
			start: 24, // MediumTokens: LeadingButtonLeadingSpace = 24.0.dp
			end: 24, // MediumTokens: LeadingButtonTrailingSpace = 24.0.dp
		},
		trailingPadding: {
			start: 15, // MediumTokens: TrailingButtonLeadingSpace = 15.0.dp
			end: 15, // MediumTokens: TrailingButtonTrailingSpace = 15.0.dp
		},
		trailingIconSize: 26, // MediumTokens: TrailingIconSize = 26.0.dp
		opticalCenterOffset: -2, // Spec: M = -2dp from center
	},
	lg: {
		containerHeight: 96, // LargeTokens: ContainerHeight = 96.0.dp
		betweenSpace: 2, // LargeTokens: BetweenSpace = 2.0.dp
		innerCorner: {
			default: cornerRadius.small, // 8dp
			hovered: 28, // LargeTokens: InnerHoveredCornerCornerSize = CornerValueLargeIncreased (28dp)
			pressed: 28, // LargeTokens: InnerPressedCornerCornerSize = CornerValueLargeIncreased (28dp)
		},
		outerCornerPercent: 50,
		leadingPadding: {
			start: 48, // LargeTokens: LeadingButtonLeadingSpace = 48.0.dp
			end: 48, // LargeTokens: LeadingButtonTrailingSpace = 48.0.dp
		},
		trailingPadding: {
			start: 29, // LargeTokens: TrailingButtonLeadingSpace = 29.0.dp
			end: 29, // LargeTokens: TrailingButtonTrailingSpace = 29.0.dp
		},
		trailingIconSize: 38, // LargeTokens: TrailingIconSize = 38.0.dp
		opticalCenterOffset: -3, // Spec: L = -3dp from center
	},
	xl: {
		containerHeight: 136, // XLargeTokens: ContainerHeight = 136.0.dp
		betweenSpace: 2, // XLargeTokens: BetweenSpace = 2.0.dp
		innerCorner: {
			default: cornerRadius.medium, // 12dp
			hovered: 28, // XLargeTokens: InnerHoveredCornerCornerSize = CornerValueLargeIncreased (28dp)
			pressed: 28, // XLargeTokens: InnerPressedCornerCornerSize = CornerValueLargeIncreased (28dp)
		},
		outerCornerPercent: 50,
		leadingPadding: {
			start: 64, // XLargeTokens: LeadingButtonLeadingSpace = 64.0.dp
			end: 64, // XLargeTokens: LeadingButtonTrailingSpace = 64.0.dp
		},
		trailingPadding: {
			start: 43, // XLargeTokens: TrailingButtonLeadingSpace = 43.0.dp
			end: 43, // XLargeTokens: TrailingButtonTrailingSpace = 43.0.dp
		},
		trailingIconSize: 50, // XLargeTokens: TrailingIconSize = 50.0.dp
		opticalCenterOffset: -6, // Spec: XL = -6dp from center
	},
} as const satisfies Record<SplitButtonSize, SplitButtonSizeTokens>;

/**
 * Leading icon size per size variant.
 * From Compose ButtonDefaults.iconSizeFor logic.
 */
export const LEADING_ICON_SIZE_FOR_SIZE = {
	xs: 18,
	sm: 20,
	md: 24,
	lg: 32,
	xl: 40,
} as const;
